/**
 * @file DataPointCategoryForm.js
 * @description Add or edit a Data Point Category. Route param categoryId === 'new' for creation.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './DataPoints.module.scss';
import { ROUTES } from '../../constants/routes';
import {
    createDataPointCategory,
    updateDataPointCategory,
} from '../../services/individualService';
import { getText } from '../../i18n';

const isNew = id => !id || id === 'new';

const DataPointCategoryForm = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const creating = isNew(categoryId);

    const [form, setForm] = useState({ name: '', code: '', enabled: true });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(!creating);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (creating) return;
        const loadCategory = async () => {
            setIsLoading(true);
            try {
                const tenantId = +window.localStorage.getItem('tenantId');
                const { individualApiHttp } =
                    await import('../../services/httpService');
                const response = await individualApiHttp.get(
                    `/v2/individual/tenant/${tenantId}/data-point-category/${categoryId}`,
                );
                const item = response.data;
                setForm({
                    name: item.name,
                    code: item.code,
                    enabled: item.enabled ?? true,
                });
            } catch {
                Toast.error(getText('msg_b70c4cf4c6a1'));
                navigate(ROUTES.DATA_POINTS);
            } finally {
                setIsLoading(false);
            }
        };
        loadCategory();
    }, [categoryId]);

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = getText('msg_d7659e2c4af2');
        if (!form.code.trim()) errs.code = getText('msg_ea6ac41a6ab6');
        return errs;
    };

    const handleSave = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setIsSaving(true);
        try {
            if (creating) {
                await createDataPointCategory({
                    name: form.name.trim(),
                    code: form.code.trim(),
                });
                Toast.success(getText('msg_d59f53275c9d'));
            } else {
                await updateDataPointCategory({
                    id: Number(categoryId),
                    name: form.name.trim(),
                    code: form.code.trim(),
                    enabled: form.enabled,
                });
                Toast.success(getText('msg_2cab8dcf38a7'));
            }
            navigate(ROUTES.DATA_POINTS);
        } catch {
            Toast.error(getText('msg_7da0c42a8ff6'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(ROUTES.DATA_POINTS);
    };

    if (isLoading) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body text-center mt-5">
                        <Spinner width={50} className={styles.loadingSpinner} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0">
                    <div className="d-flex justify-content-between align-items-center">
                        <p className="page-title float-start ms-4">
                            {creating
                                ? getText('msg_327a0f8b2f82')
                                : getText('msg_29bd70929a5a')}
                        </p>
                    </div>

                    <form
                        className="p-3"
                        style={{ width: '567px', margin: 'auto' }}
                        onSubmit={e => e.preventDefault()}
                    >
                        <div className="form-group mb-3">
                            <label htmlFor="dp-cat-form-name">
                                {getText('msg_709a23220f2c')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                id="dp-cat-form-name"
                                type="text"
                                maxLength={128}
                                autoFocus
                                className={classNames('form-control', {
                                    'is-invalid': errors.name,
                                })}
                                value={form.name}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                            />
                            {errors.name && (
                                <div className="invalid-feedback">
                                    {errors.name}
                                </div>
                            )}
                        </div>

                        <div className="form-group mb-3">
                            <label htmlFor="dp-cat-form-code">
                                {getText('msg_adac69379a62')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                id="dp-cat-form-code"
                                type="text"
                                maxLength={128}
                                disabled={!creating}
                                className={classNames('form-control', {
                                    'is-invalid': errors.code,
                                })}
                                value={form.code}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        code: e.target.value,
                                    }))
                                }
                            />
                            {errors.code && (
                                <div className="invalid-feedback">
                                    {errors.code}
                                </div>
                            )}
                        </div>
                    </form>

                    <div className="text-center mt-2">
                        <span
                            type="button"
                            onClick={handleCancel}
                            className="ing-text-buttons d-inline me-3"
                        >
                            <i className="bi bi-x me-2" />
                            {getText('msg_77dfd2135f4d')}
                        </span>
                        <span
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="ing-text-buttons d-inline ms-3"
                        >
                            <i className="bi bi-check-lg me-2" />
                            {getText('msg_efc007a393f6')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataPointCategoryForm;
